import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from './-global.min.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
  }

  constructor() {
    super()

    this.type = this.dataset.type || ''
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
      document.body.style.overflow = 'hidden'
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      document.body.style.overflow = ''
      return
    }

    if (event.code.toUpperCase() === 'ESCAPE') {
      this.open = false
      document.body.style.overflow = ''
    }
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      class="${this.dataset.type === 'modal' ? 'has-bg': ''}"
    >
      <div class="${this.dataset.type === 'modal' ? 'modal__bg': 'hidden'}"></div>
      <button
        @click="${this.close}"
        class="button"
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        ${unsafeHTML(paths.close)}
      </button>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      width: 4rem;
      position: fixed;
      top: 4rem;
      right: 4rem;
      display: flex;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 1.2rem;
      z-index: 102;
    }
    .button svg {
    min-height:30px;
}
    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }

    .modal__bg {
      height: 100%;
      left: 0;
      position: fixed;
      top: 0;
      width: 100%;
      background-color: #000;
      opacity: .4;
      z-index: 100;
    }

    .has-bg {
      rid-column-end: -1;
      grid-column-start: 1;
      height: 100%;
      position: fixed;
      width: 100%;
      grid-column-gap: 0.5rem;
      display: grid;
      grid-column: full;
      grid-template-columns: [gut-full-start] .5rem [gut-main-start] repeat(12,1fr) [gut-main-end] .5rem [gut-full-end];
    }

    .has-bg .content {
      align-self: flex-start;
      background-color: #fff;
      display: flex;
      grid-column-end: 14;
      grid-column-start: 2;
      height: 43rem;
      margin-top: 1rem;
      position: relative;
      align-items: flex-start;
      justify-content: flex-start;
      padding: 0;
      z-index: 101;
    }

    .has-bg .button {
      top: 1rem;
      right: 1rem;
      border: none;
    }

    @media only screen and (min-width: 1024px) {
      .has-bg .content {
        align-self: center;
        height: 32.5rem;
        margin-top: 0;
        grid-column-end: 12;
        grid-column-start: 4;
      }

      .has-bg .button {
        top: 50%;
        right: 50%;
        transform: translate(32vw, -16.25rem);
      }
    }

    @media only screen and (min-width: 1192px) {
      .has-bg {
        grid-column-gap: 1rem;
        grid-template-columns: [gut-full-start] 1.5rem [gut-main-start] repeat(12,1fr) [gut-main-end] 1.5rem [gut-full-end];
      }

      .has-bg .content {
        grid-column-end: 11;
        grid-column-start: 5;
      }

      .has-bg .button {
        transform: translate(23vw, -16.25rem);
      }
    }

    @media only screen and (min-width: 1440px) {
      .has-bg .button {
        transform: translate(23.5vw, -16.25rem);
      }
    }
  `
}

customElements.define('hh-modal', HHModal)
